import React, { useEffect } from 'react';
import { createTheme, ThemeProvider as MaterialThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import themeOptions from 'styles/theme';
import { useAppSelector } from 'store';
import { selectLanguageIsRtl } from 'store/languages';

/**
 * Two emotion caches, one per direction (Phase D1, replaces jss + jss-rtl).
 * The rtl cache runs stylis-plugin-rtl after the prefixer, which is how MUI
 * flips its own styles; the app's SCSS is flipped by postcss-rtl at build
 * time, exactly as before. `prepend: true` inserts the emotion <style> tags at
 * the top of <head>, so the SCSS bundle keeps winning the cascade -- the same
 * order the old `jss-insertion-point` comment produced.
 */
const cacheLtr = createCache({ key: 'mui', prepend: true });
const cacheRtl = createCache({
  key: 'muirtl',
  prepend: true,
  stylisPlugins: [prefixer, rtlPlugin],
});

export const themeRTL = createTheme({ ...themeOptions, direction: 'rtl' });
export const themeLTR = createTheme({ ...themeOptions, direction: 'ltr' });

export const ThemeProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const isRtl = useAppSelector(selectLanguageIsRtl);

  useEffect(() => {
    const el = document.getElementsByTagName('html')[0];
    el?.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
  }, [isRtl]);

  return (
    <CacheProvider value={isRtl ? cacheRtl : cacheLtr}>
      <MaterialThemeProvider theme={isRtl ? themeRTL : themeLTR}>
        <CssBaseline />
        {children}
      </MaterialThemeProvider>
    </CacheProvider>
  );
};
